import React, { useEffect, useState } from 'react';
import { ActivityIndicator, StyleSheet, View } from 'react-native';
import { Text } from '../Texte';
import { LinearGradient } from 'expo-linear-gradient';
import Animated, {
  cancelAnimation,
  Easing,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';
import { Apparition } from './Apparition';
import { CircleAlert, CircleCheck } from '../icons';
import { t } from '../../i18n';
import { interfaceRtl } from '../../i18n/sens';
import { colors, gradients, radius, spacing, typography } from '../../theme/tokens';

export type Etape = 'analyse' | 'cv' | 'lettre' | 'controle';
export type Etat = 'attente' | 'en_cours' | 'fait' | 'erreur';

export interface FilEtapesProps {
  etats: Record<Etape, Etat>;
  detail: (e: Etape) => string;
}

const PASTILLE = 26;

function Icone({ etat }: { etat: Etat }) {
  if (etat === 'fait') {
    return (
      <Apparition effet="pop">
        <CircleCheck size={PASTILLE} color={colors.success} />
      </Apparition>
    );
  }
  if (etat === 'en_cours') return <ActivityIndicator color={colors.cyan} style={styles.pastille} />;
  // Ambre et non corail : l'écran n'emploie plus le corail (consigne du 04/10/2026).
  if (etat === 'erreur') return <CircleAlert size={PASTILLE} color={colors.warning} />;
  return <View style={[styles.pastille, styles.cercle]} />;
}

/** Léger reflet qui balaie l'étape en cours. Absent si les animations sont réduites. */
function Reflet({ largeur }: { largeur: number }) {
  const x = useSharedValue(0);
  useEffect(() => {
    x.value = withRepeat(withTiming(1, { duration: 1800, easing: Easing.inOut(Easing.quad) }), -1, false);
    return () => cancelAnimation(x);
  }, [x]);
  // Les translations ne suivent pas le sens d'écriture : en droite à gauche, le reflet part de la droite.
  const sens = interfaceRtl() ? -1 : 1;
  const style = useAnimatedStyle(() => ({ transform: [{ translateX: sens * (-largeur + x.value * largeur * 2) }] }));
  return (
    <Animated.View style={[StyleSheet.absoluteFill, style]} pointerEvents="none">
      <LinearGradient colors={['transparent', colors.refletCyan, 'transparent']} start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }} style={StyleSheet.absoluteFill} />
    </Animated.View>
  );
}

function Carte({ etape, etat, detail, compacte }: { etape: Etape; etat: Etat; detail: string; compacte?: boolean }) {
  const reduit = useReducedMotion();
  const [largeur, setLargeur] = useState(0);
  const enCours = etat === 'en_cours';
  const carte = (
    <View
      style={[styles.carte, compacte && !enCours && styles.carteCompacte, enCours && styles.carteDansBord]}
      onLayout={(e) => setLargeur(e.nativeEvent.layout.width)}
      accessible
      accessibilityLabel={`${t(`offres.preparation.etapes.${etape}`)}, ${detail}`}
      testID={`etape-${etape}-${etat}`}
    >
      {enCours && !reduit && largeur > 0 ? <Reflet largeur={largeur} /> : null}
      <Icone etat={etat} />
      <View style={styles.textes}>
        <Text style={[styles.titre, etat === 'attente' && styles.attenue]} numberOfLines={compacte ? 2 : 1}>
          {t(`offres.preparation.etapes.${etape}`)}
        </Text>
        <Text style={[styles.detail, etat === 'attente' && styles.attenue]} numberOfLines={1}>
          {detail}
        </Text>
      </View>
    </View>
  );
  if (!enCours) return carte;
  // Contour en dégradé d'accent : React Native n'a pas de bordure en dégradé, d'où ce cadre d'un
  // pixel et demi peint en dégradé, la carte posée dedans.
  return (
    <LinearGradient colors={[...gradients.accent]} start={{ x: 0, y: 0 }} end={{ x: 1, y: 1 }} style={[styles.bord, compacte && styles.bordCompact]} testID={`bord-accent-${etape}`}>
      {carte}
    </LinearGradient>
  );
}

/**
 * Les quatre étapes sur un fil vertical. CV et lettre partent ensemble après l'analyse : ils sont
 * côte à côte, pour qu'on voie qu'ils avancent en parallèle. Le fil se remplit en dégradé d'accent à mesure que
 * le serveur rend les étapes — jamais avant.
 */
export function FilEtapes({ etats, detail }: FilEtapesProps) {
  const reduit = useReducedMotion();
  const rempli = (etats.analyse === 'fait' ? 0.5 : 0) + (etats.cv === 'fait' && etats.lettre === 'fait' ? 0.5 : 0);
  const part = useSharedValue(rempli);
  useEffect(() => {
    part.value = reduit ? rempli : withTiming(rempli, { duration: 500, easing: Easing.inOut(Easing.cubic) });
  }, [rempli, reduit, part]);
  const fil = useAnimatedStyle(() => ({ height: `${part.value * 100}%` }));

  return (
    <View style={styles.fil}>
      <View style={styles.rail} pointerEvents="none">
        <Animated.View style={[styles.railRempli, fil]} testID="fil-rempli">
          <LinearGradient colors={[...gradients.accent]} style={StyleSheet.absoluteFill} />
        </Animated.View>
      </View>
      <Carte etape="analyse" etat={etats.analyse} detail={detail('analyse')} />
      <View style={styles.parallele}>
        <Text style={styles.legende}>{t('offres.preparation.parallele')}</Text>
        <View style={styles.paire}>
          <Carte etape="cv" etat={etats.cv} detail={detail('cv')} compacte />
          <Carte etape="lettre" etat={etats.lettre} detail={detail('lettre')} compacte />
        </View>
      </View>
      <Carte etape="controle" etat={etats.controle} detail={detail('controle')} />
    </View>
  );
}

const styles = StyleSheet.create({
  fil: { gap: spacing.sm },
  rail: { position: 'absolute', start: spacing.md + PASTILLE / 2 - 1, top: spacing.lg, bottom: spacing.lg, width: 2, backgroundColor: colors.inkSoft },
  railRempli: { width: 2, overflow: 'hidden' },
  carte: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: radius.lg,
    backgroundColor: colors.inkSoft,
    borderWidth: 1,
    borderColor: colors.inkSoft,
    overflow: 'hidden',
  },
  carteCompacte: { flex: 1, gap: spacing.sm, paddingHorizontal: spacing.sm },
  bord: { borderRadius: radius.lg, padding: 1.5 },
  bordCompact: { flex: 1 },
  // Dans le cadre en dégradé : même carte, sans sa propre bordure, coins ajustés au cadre.
  carteDansBord: { borderWidth: 0, borderRadius: radius.lg - 1.5, gap: spacing.sm, paddingHorizontal: spacing.sm },
  pastille: { width: PASTILLE, height: PASTILLE },
  cercle: { borderRadius: radius.pill, borderWidth: 2, borderColor: colors.grey400 },
  textes: { flex: 1, gap: 2 },
  titre: { fontSize: typography.small.size, lineHeight: typography.small.lineHeight, fontWeight: '700', color: colors.white },
  detail: { fontSize: typography.small.size, lineHeight: typography.small.lineHeight, color: colors.grey200 },
  // Atténuée par la COULEUR, pas par l'opacité : grey400 sur bleu nuit garde un contraste AA.
  attenue: { color: colors.grey400 },
  parallele: { gap: spacing.xs, paddingStart: spacing.xxl },
  legende: { fontSize: typography.caption.size, lineHeight: typography.caption.lineHeight, letterSpacing: typography.caption.letterSpacing, fontWeight: '600', color: colors.grey400 },
  paire: { flexDirection: 'row', gap: spacing.sm },
});
